import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import {
  CallDetailState,
  callLogsDetailState,
  callLogsGetExtensionState,
  CallLogsState,
  CallStatsState,
  CustomerInfoModalState,
  CustomerInfoState,
  IvrMenusResponse,
  ivrMenusState,
  ModalState,
  QueueCallsData,
  QueueCallsState,
  syncExtensionsState,
  syncIvrMenusState,
  UpdateCallLogState,
} from "src/interfaces";

const initialState: CallLogsState = {
  loading: false,
  error: null,
  data: {
    data: [],
    current_page: 0,
    first_page_url: "",
    from: 0,
    last_page: 0,
    last_page_url: "",
    links: [],
    next_page_url: null,
    path: "",
    per_page: 0,
    prev_page_url: null,
    to: 0,
    total: 0,
  },
};

const syncIvrMenusInitialState: syncIvrMenusState = {
  loading: false,
  error: null,
  data: {
    success: false,
    message: null,
  },
};

const syncExtensionsInitialState: syncExtensionsState = {
  loading: false,
  error: null,
  data: {
    success: false,
    message: null,
  },
};

const ivrMenusInitialState: ivrMenusState = {
  loading: false,
  error: null,
  data: {
    success: false,
    data: [],
  },
};

const customerInfoInitialState: CustomerInfoState = {
  loading: false,
  error: null,
  data: {
    status: null,
    message: null,
    result: null,
  },
};

const modalInitialState: ModalState = {
  isOpen: false,
};

const customerInfoModalInitialState: CustomerInfoModalState = {
  shouldFetchCustomerInfo: false,
  // selectedQueryId: null,
};

const callDetailInitialState: CallDetailState = {
  loading: false,
  error: null,
  data: {
    status: null,
    message: null,
    call: null,
  },
};

const updateCallInitialState: UpdateCallLogState = {
  loading: false,
  error: null,
  data: {
    success: null,
    message: null,
    call: null,
  },
};

const queueCallsInitialState: QueueCallsState = {
  loading: false,
  error: null,
  data: {
    data: [],
    pagination: null,
  },
};

const callStatsInitialState: CallStatsState = {
  loading: false,
  error: null,
  data: {
    total_calls_today: 0,
    avg_call_duration: "",
    answer_rate: 0,
    queries_created: 0,
  },
};

const callLogsDetailInitialState: callLogsDetailState = {
  loading: false,
  error: null,
  data: {
    success: null,
    message: null,
    call: null,
    logs: null,
    pagination: null,
    customer_info: null,
  },
};

const callLogsGetExtensionInitialState: callLogsGetExtensionState = {
  loading: false,
  error: null,
  data: {
    status: null,
    message: null,
    result: [],
  },
};

const callLogsSlice = createSlice({
  name: "callLogs",
  initialState,
  reducers: {
    fetchCallLogsRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchCallLogsSuccess: (
      state,
      action: PayloadAction<CallLogsState["data"]>
    ) => {
      state.loading = false;
      state.error = null;
      state.data = action.payload;
    },
    fetchCallLogsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

const ivrMenusSlice = createSlice({
  name: "ivrMenus",
  initialState: ivrMenusInitialState,
  reducers: {
    fetchIvrMenusRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchIvrMenusSuccess: (state, action: PayloadAction<IvrMenusResponse>) => {
      state.loading = false;
      state.error = null;
      state.data = action.payload;
    },
    fetchIvrMenusFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

const syncIvrMenusSlice = createSlice({
  name: "syncIvrMenus",
  initialState: syncIvrMenusInitialState,
  reducers: {
    syncIvrMenusRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    syncIvrMenusSuccess: (
      state,
      action: PayloadAction<{ success: boolean; message?: string | null }>
    ) => {
      state.loading = false;
      state.error = null;
      state.data = action.payload;
    },
    syncIvrMenusFailure: (state, action: PayloadAction<string | null>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

const syncExtensionsSlice = createSlice({
  name: "syncExtensions",
  initialState: syncExtensionsInitialState,
  reducers: {
    syncExtensionsRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    syncExtensionsSuccess: (
      state,
      action: PayloadAction<{ message?: string; count?: number }>
    ) => {
      state.loading = false;
      state.error = null;
      state.data = action.payload;
    },
    syncExtensionsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

const customerInfoSlice = createSlice({
  name: "customerInfo",
  initialState: customerInfoInitialState,
  reducers: {
    fetchCustomerInfoRequest: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    fetchCustomerInfoSuccess: (
      state,
      action: PayloadAction<CustomerInfoState["data"]>
    ) => {
      state.loading = false;
      state.error = null;
      state.data = action.payload;
    },
    fetchCustomerInfoFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

const modalSlice = createSlice({
  name: "modal",
  initialState: modalInitialState,
  reducers: {
    openModal: (state) => {
      state.isOpen = true;
    },
    closeModal: (state) => {
      state.isOpen = false;
    },
    setModal: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload;
    },
  },
});

const customerInfoModalSlice = createSlice({
  name: "customerInfoModal",
  initialState: customerInfoModalInitialState,
  reducers: {
    enableFetch: (state) => {
      state.shouldFetchCustomerInfo = true;
    },
    disableFetch: (state) => {
      state.shouldFetchCustomerInfo = false;
    },
    setFetchStatus: (state, action: PayloadAction<boolean>) => {
      state.shouldFetchCustomerInfo = action.payload;
    },
    // setSelectedQueryId: (state, action: PayloadAction<string | number>) => {
    //   state.selectedQueryId = action.payload;
    // },
  },
});

const callDetailSlice = createSlice({
  name: "callDetail",
  initialState: callDetailInitialState,
  reducers: {
    fetchCallDetailRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchCallDetailSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.data = action.payload;
    },
    fetchCallDetailFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

const updateCallSlice = createSlice({
  name: "updateCall",
  initialState: updateCallInitialState,
  reducers: {
    updateCallRequest: (state) => {
      state.loading = true;
      state.error = null;
      // reset status on new request

      state.data = {
        success: null,
        message: null,
        call: null,
      };
    },
    updateCallSuccess: (
      state,
      action: PayloadAction<UpdateCallLogState["data"]>
    ) => {
      state.loading = false;
      state.error = null;
      state.data = action.payload; // update data with API response
    },
    updateCallFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.data.success = null; // mark failure
    },
    clearUpdateCall: (state) => {
      state.loading = false;
      state.error = null;

      state.data = {
        success: null,
        message: null,
        call: null,
      };
    },
  },
});

const queueCallsSlice = createSlice({
  name: "queueCalls",
  initialState: queueCallsInitialState,
  reducers: {
    fetchQueueCallsRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchQueueCallsSuccess: (state, action: PayloadAction<QueueCallsData>) => {
      state.loading = false;
      state.error = null;
      state.data = action.payload;
    },
    fetchQueueCallsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

const callStatsSlice = createSlice({
  name: "callStats",
  initialState: callStatsInitialState,
  reducers: {
    fetchCallStatsRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchCallStatsSuccess: (
      state,
      action: PayloadAction<CallStatsState["data"]>
    ) => {
      state.loading = false;
      state.data = action.payload;
    },
    fetchCallStatsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

const callLogsDetailSlice = createSlice({
  name: "callLogsDetail",
  initialState: callLogsDetailInitialState,
  reducers: {
    fetchCallLogsDetailRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchCallLogsDetailSuccess: (
      state,
      action: PayloadAction<callLogsDetailState["data"]>
    ) => {
      state.loading = false;
      state.data = action.payload;
    },
    fetchCallLogsDetailFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

const callLogsGetExtensionSlice = createSlice({
  name: "callLogsGetExtension",
  initialState: callLogsGetExtensionInitialState,
  reducers: {
    fetchCallLogsGetExtensionRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchCallLogsGetExtensionSuccess: (
      state,
      action: PayloadAction<callLogsGetExtensionState["data"]>
    ) => {
      state.loading = false;
      state.error = null;
      state.data = action.payload;
    },
    fetchCallLogsGetExtensionFailure: (
      state,
      action: PayloadAction<string>
    ) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const {
  fetchIvrMenusRequest,
  fetchIvrMenusSuccess,
  fetchIvrMenusFailure,
} = ivrMenusSlice.actions;

export const {
  fetchCallLogsRequest,
  fetchCallLogsSuccess,
  fetchCallLogsFailure,
} = callLogsSlice.actions;

export const {
  syncExtensionsRequest,
  syncExtensionsSuccess,
  syncExtensionsFailure,
} = syncExtensionsSlice.actions;

export const { syncIvrMenusRequest, syncIvrMenusSuccess, syncIvrMenusFailure } =
  syncIvrMenusSlice.actions;

export const {
  fetchCustomerInfoRequest,
  fetchCustomerInfoSuccess,
  fetchCustomerInfoFailure,
} = customerInfoSlice.actions;

export const { openModal, closeModal, setModal } = modalSlice.actions;
export const {
  enableFetch,
  disableFetch,
  setFetchStatus,
  // setSelectedQueryId
} = customerInfoModalSlice.actions;

export const {
  updateCallRequest,
  updateCallSuccess,
  updateCallFailure,
  clearUpdateCall,
} = updateCallSlice.actions;
export const {
  fetchQueueCallsRequest,
  fetchQueueCallsSuccess,
  fetchQueueCallsFailure,
} = queueCallsSlice.actions;

export const {
  fetchCallDetailRequest,
  fetchCallDetailSuccess,
  fetchCallDetailFailure,
} = callDetailSlice.actions;

export const {
  fetchCallStatsRequest,
  fetchCallStatsSuccess,
  fetchCallStatsFailure,
} = callStatsSlice.actions;

export const {
  fetchCallLogsDetailRequest,
  fetchCallLogsDetailSuccess,
  fetchCallLogsDetailFailure,
} = callLogsDetailSlice.actions;

export const {
  fetchCallLogsGetExtensionRequest,
  fetchCallLogsGetExtensionSuccess,
  fetchCallLogsGetExtensionFailure,
} = callLogsGetExtensionSlice.actions;

export const selectModalOpen = (state: RootState) => state.ivrModal.isOpen;
export const selectShouldFetchCustomer = (state: RootState) =>
  state.customerInfoModal.shouldFetchCustomerInfo;
// export const selectSelectedQueryId = (state: RootState) =>
//   state.customerInfoModal.selectedQueryId;
// Export selectors
export const selectCallLogs = (
  state: RootState & { callLogs: CallLogsState }
) => state.callLogs;
export const selectIvrMenus = (
  state: RootState & { ivrMenus: ivrMenusState }
) => state.ivrMenus;
export const selectSyncIvrMenus = (
  state: RootState & { syncIvrMenus: syncIvrMenusState }
) => state.syncIvrMenus;
export const selectSyncExtensions = (
  state: RootState & { syncExtensions: syncExtensionsState }
) => state.syncExtensions;
export const selectCustomerInfo = (
  state: RootState & { customerInfo: CustomerInfoState }
) => state.customerInfo;

export const selectCallDetail = (
  state: RootState & { callDetail: CallDetailState }
) => state.callDetail;

export const selectUpdateCall = (state: RootState) => state.updateCall;
export const selectQueueCalls = (state: RootState) => state.queueCalls;
export const selectCallStats = (state: RootState) => state.callStats;
export const selectCallLogsDetails = (state: RootState) => state.callLogsDetail;
export const selectCallLogsGetExtension = (state: RootState) =>
  state.callLogsGetExtension;

// Export reducers
export default {
  callLogs: callLogsSlice.reducer,
  ivrMenus: ivrMenusSlice.reducer,
  syncIvrMenus: syncIvrMenusSlice.reducer,
  syncExtensions: syncExtensionsSlice.reducer,
  ivrModal: modalSlice.reducer,
  customerInfo: customerInfoSlice.reducer,
  customerInfoModal: customerInfoModalSlice.reducer,
  callDetail: callDetailSlice.reducer,
  updateCall: updateCallSlice.reducer,
  queueCalls: queueCallsSlice.reducer,
  callStats: callStatsSlice.reducer,
  callLogsDetail: callLogsDetailSlice.reducer,
  callLogsGetExtension: callLogsGetExtensionSlice.reducer,
};
